import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseFraction,
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

const pick = (items) => items[Math.floor(Math.random() * items.length)];

const stripUnits = (raw) =>
  String(raw || "")
    .toLowerCase()
    .replace(/,/g, ".")
    .replace(/[^0-9./-]+/g, "")
    .trim();

const parseUserNumber = (raw) => parseFraction(stripUnits(raw));
const closeTo = (a, b) => Number.isFinite(a) && Math.abs(a - b) < 0.01;
const numberLatex = (value) => String(value).replace(".", "{,}");
const unitBase = (unit) => unit.replace(/\^\d$/, "");
const unitLatex = (unit) => {
  if (unit.endsWith("^2")) return "\\text{" + unit.slice(0, -2) + "}^2";
  if (unit.endsWith("^3")) return "\\text{" + unit.slice(0, -2) + "}^3";
  return "\\text{" + unit + "}";
};
const valueWithUnit = (value, unit) => `${numberLatex(value)}\\,${unitLatex(unit)}`;
const powerConversionLatex = (data, power, direction) => {
  const linearFactor = Math.round(Math.pow(data.factor, 1 / power));
  const fromBase = unitBase(data.fromUnit);
  const toBase = unitBase(data.toUnit);
  const exponent = power === 2 ? "2" : "3";
  const largerBase = direction === "toLarger" ? toBase : fromBase;
  const smallerBase = direction === "toLarger" ? fromBase : toBase;
  const largerUnit = direction === "toLarger" ? data.toUnit : data.fromUnit;
  const smallerUnit = direction === "toLarger" ? data.fromUnit : data.toUnit;
  return String.raw`1\,\text{${largerBase}}=${linearFactor}\,\text{${smallerBase}},\quad 1\,${unitLatex(largerUnit)}=${linearFactor}^${exponent}\,${unitLatex(smallerUnit)}=${data.factor}\,${unitLatex(smallerUnit)}`;
};

function UnitInput({ userAnswer, onChange, disabled, label, suffixLatex, placeholder }) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  return (
    <FractionInput
      value={raw}
      onChange={(value) =>
        onChange({
          raw: value?.raw ?? "",
          parsed: parseUserNumber(value?.raw ?? ""),
        })
      }
      disabled={disabled}
      label={label}
      suffixLatex={suffixLatex}
      placeholder={placeholder}
    />
  );
}

function singleInputTask(config) {
  return {
    generate: () => ({ data: pick(config.variants) }),
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          {config.renderTask(data)}
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return (
        <UnitInput
          userAnswer={userAnswer}
          onChange={onChange}
          disabled={disabled}
          label={config.label}
          suffixLatex={config.suffixLatex}
          placeholder={config.placeholder}
        />
      );
    },
    answer: ({ data }) => data.answer,
    answerLabel: config.label,
    answerSuffix: config.answerSuffix,
    renderAnswer: ({ data }) => (
      <InlineMath math={`${config.label}=${numberLatex(data.answer)}${config.answerSuffix}`} />
    ),
    renderExplanation({ data }) {
      return config.renderExplanation(data);
    },
  };
}

const taskRegistry = {
  LengthSmallToLargeTask: singleInputTask({
    label: "d",
    suffixLatex: String.raw`\text{m}`,
    answerSuffix: String.raw`\,\mathrm{m}`,
    placeholder: "np. 3,5",
    variants: [
      { from: 350, fromUnit: "cm", answer: 3.5, factor: 100, toUnit: "m" },
      { from: 4200, fromUnit: "mm", answer: 4.2, factor: 1000, toUnit: "m" },
      { from: 75, fromUnit: "dm", answer: 7.5, factor: 10, toUnit: "m" },
      { from: 1250, fromUnit: "cm", answer: 12.5, factor: 100, toUnit: "m" },
    ],
    renderTask: (data) => (
      <>Zamień <InlineMath math={valueWithUnit(data.from, data.fromUnit)} /> na metry.</>
    ),
    renderExplanation: (data) => (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p><strong>Krok 1.</strong> Przechodzimy do większej jednostki, więc dzielimy.</p>
        <BlockMath math={`${valueWithUnit(data.from, data.fromUnit)}:${data.factor}=${valueWithUnit(data.answer, data.toUnit)}`} />
      </div>
    ),
  }),

  LengthLargeToSmallTask: singleInputTask({
    label: "d",
    suffixLatex: String.raw`\text{cm}`,
    answerSuffix: String.raw`\,\mathrm{cm}`,
    placeholder: "np. 250",
    variants: [
      { from: 2.5, fromUnit: "m", answer: 250, factor: 100, toUnit: "cm" },
      { from: 1.2, fromUnit: "km", answer: 120000, factor: 100000, toUnit: "cm" },
      { from: 3.4, fromUnit: "dm", answer: 34, factor: 10, toUnit: "cm" },
      { from: 0.8, fromUnit: "m", answer: 80, factor: 100, toUnit: "cm" },
    ],
    renderTask: (data) => (
      <>Zamień <InlineMath math={valueWithUnit(data.from, data.fromUnit)} /> na centymetry.</>
    ),
    renderExplanation: (data) => (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p><strong>Krok 1.</strong> Przechodzimy do mniejszej jednostki, więc mnożymy.</p>
        <BlockMath math={`${valueWithUnit(data.from, data.fromUnit)}\\cdot ${data.factor}=${valueWithUnit(data.answer, data.toUnit)}`} />
      </div>
    ),
  }),

  AreaSmallToLargeTask: singleInputTask({
    label: "P",
    suffixLatex: String.raw`\text{m}^2`,
    answerSuffix: String.raw`\,\mathrm{m}^2`,
    placeholder: "np. 2,5",
    variants: [
      { from: 25000, fromUnit: "cm^2", answer: 2.5, factor: 10000, toUnit: "m^2" },
      { from: 360000, fromUnit: "cm^2", answer: 36, factor: 10000, toUnit: "m^2" },
      { from: 1200, fromUnit: "dm^2", answer: 12, factor: 100, toUnit: "m^2" },
      { from: 8500, fromUnit: "dm^2", answer: 85, factor: 100, toUnit: "m^2" },
    ],
    renderTask: (data) => (
      <>Zamień <InlineMath math={valueWithUnit(data.from, data.fromUnit)} /> na metry kwadratowe.</>
    ),
    renderExplanation: (data) => (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p><strong>Krok 1.</strong> Przy polu przelicznik jednostek długości podnosimy do kwadratu.</p>
        <BlockMath math={powerConversionLatex(data, 2, "toLarger")} />
        <p><strong>Krok 2.</strong> Skoro przechodzimy do większej jednostki pola, dzielimy przez ten przelicznik.</p>
        <BlockMath math={`${valueWithUnit(data.from, data.fromUnit)}:${data.factor}=${valueWithUnit(data.answer, data.toUnit)}`} />
      </div>
    ),
  }),

  AreaLargeToSmallTask: singleInputTask({
    label: "P",
    suffixLatex: String.raw`\text{cm}^2`,
    answerSuffix: String.raw`\,\mathrm{cm}^2`,
    placeholder: "np. 30000",
    variants: [
      { from: 3, fromUnit: "m^2", answer: 30000, factor: 10000, toUnit: "cm^2" },
      { from: 1.5, fromUnit: "m^2", answer: 15000, factor: 10000, toUnit: "cm^2" },
      { from: 24, fromUnit: "dm^2", answer: 2400, factor: 100, toUnit: "cm^2" },
      { from: 0.75, fromUnit: "m^2", answer: 7500, factor: 10000, toUnit: "cm^2" },
    ],
    renderTask: (data) => (
      <>Zamień <InlineMath math={valueWithUnit(data.from, data.fromUnit)} /> na centymetry kwadratowe.</>
    ),
    renderExplanation: (data) => (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p><strong>Krok 1.</strong> Przy polu przelicznik jednostek długości podnosimy do kwadratu.</p>
        <BlockMath math={powerConversionLatex(data, 2, "toSmaller")} />
        <p><strong>Krok 2.</strong> Skoro przechodzimy do mniejszej jednostki pola, mnożymy przez ten przelicznik.</p>
        <BlockMath math={`${valueWithUnit(data.from, data.fromUnit)}\\cdot ${data.factor}=${valueWithUnit(data.answer, data.toUnit)}`} />
      </div>
    ),
  }),

  VolumeSmallToLargeTask: singleInputTask({
    label: "V",
    suffixLatex: String.raw`\text{dm}^3`,
    answerSuffix: String.raw`\,\mathrm{dm}^3`,
    placeholder: "np. 2,5",
    variants: [
      { from: 2500, fromUnit: "cm^3", answer: 2.5, factor: 1000, toUnit: "dm^3" },
      { from: 7500, fromUnit: "cm^3", answer: 7.5, factor: 1000, toUnit: "dm^3" },
      { from: 12000, fromUnit: "cm^3", answer: 12, factor: 1000, toUnit: "dm^3" },
      { from: 3500, fromUnit: "cm^3", answer: 3.5, factor: 1000, toUnit: "dm^3" },
    ],
    renderTask: (data) => (
      <>Zamień <InlineMath math={valueWithUnit(data.from, data.fromUnit)} /> na decymetry sześcienne.</>
    ),
    renderExplanation: (data) => (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p><strong>Krok 1.</strong> Przy objętości przelicznik jednostek długości podnosimy do trzeciej potęgi.</p>
        <BlockMath math={powerConversionLatex(data, 3, "toLarger")} />
        <p><strong>Krok 2.</strong> Skoro przechodzimy do większej jednostki objętości, dzielimy przez ten przelicznik.</p>
        <BlockMath math={`${valueWithUnit(data.from, data.fromUnit)}:${data.factor}=${valueWithUnit(data.answer, data.toUnit)}`} />
      </div>
    ),
  }),

  VolumeLargeToSmallTask: singleInputTask({
    label: "V",
    suffixLatex: String.raw`\text{cm}^3`,
    answerSuffix: String.raw`\,\mathrm{cm}^3`,
    placeholder: "np. 3000",
    variants: [
      { from: 3, fromUnit: "dm^3", answer: 3000, factor: 1000, toUnit: "cm^3" },
      { from: 1.5, fromUnit: "dm^3", answer: 1500, factor: 1000, toUnit: "cm^3" },
      { from: 0.75, fromUnit: "dm^3", answer: 750, factor: 1000, toUnit: "cm^3" },
      { from: 2.4, fromUnit: "dm^3", answer: 2400, factor: 1000, toUnit: "cm^3" },
    ],
    renderTask: (data) => (
      <>Zamień <InlineMath math={valueWithUnit(data.from, data.fromUnit)} /> na centymetry sześcienne.</>
    ),
    renderExplanation: (data) => (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p><strong>Krok 1.</strong> Przy objętości przelicznik jednostek długości podnosimy do trzeciej potęgi.</p>
        <BlockMath math={powerConversionLatex(data, 3, "toSmaller")} />
        <p><strong>Krok 2.</strong> Skoro przechodzimy do mniejszej jednostki objętości, mnożymy przez ten przelicznik.</p>
        <BlockMath math={`${valueWithUnit(data.from, data.fromUnit)}\\cdot ${data.factor}=${valueWithUnit(data.answer, data.toUnit)}`} />
      </div>
    ),
  }),

  MassSmallToLargeTask: singleInputTask({
    label: "m",
    suffixLatex: String.raw`\text{kg}`,
    answerSuffix: String.raw`\,\mathrm{kg}`,
    placeholder: "np. 2,5",
    variants: [
      { from: 2500, fromUnit: "g", answer: 2.5, factor: 1000, toUnit: "kg" },
      { from: 75, fromUnit: "dag", answer: 0.75, factor: 100, toUnit: "kg" },
      { from: 125000, fromUnit: "g", answer: 125, factor: 1000, toUnit: "kg" },
      { from: 4500, fromUnit: "g", answer: 4.5, factor: 1000, toUnit: "kg" },
    ],
    renderTask: (data) => (
      <>Zamień <InlineMath math={valueWithUnit(data.from, data.fromUnit)} /> na kilogramy.</>
    ),
    renderExplanation: (data) => (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p><strong>Krok 1.</strong> Przechodzimy do większej jednostki masy, więc dzielimy.</p>
        <BlockMath math={`${valueWithUnit(data.from, data.fromUnit)}:${data.factor}=${valueWithUnit(data.answer, data.toUnit)}`} />
      </div>
    ),
  }),

  MassLargeToSmallTask: singleInputTask({
    label: "m",
    suffixLatex: String.raw`\text{g}`,
    answerSuffix: String.raw`\,\mathrm{g}`,
    placeholder: "np. 2500",
    variants: [
      { from: 2.5, fromUnit: "kg", answer: 2500, factor: 1000, toUnit: "g" },
      { from: 1.2, fromUnit: "t", answer: 1200000, factor: 1000000, toUnit: "g" },
      { from: 35, fromUnit: "dag", answer: 350, factor: 10, toUnit: "g" },
      { from: 0.8, fromUnit: "kg", answer: 800, factor: 1000, toUnit: "g" },
    ],
    renderTask: (data) => (
      <>Zamień <InlineMath math={valueWithUnit(data.from, data.fromUnit)} /> na gramy.</>
    ),
    renderExplanation: (data) => (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p><strong>Krok 1.</strong> Przechodzimy do mniejszej jednostki masy, więc mnożymy.</p>
        <BlockMath math={`${valueWithUnit(data.from, data.fromUnit)}\\cdot ${data.factor}=${valueWithUnit(data.answer, data.toUnit)}`} />
      </div>
    ),
  }),
};

function moduleFor(task) {
  const mod = taskRegistry[task?.type];
  if (!mod) throw new Error(`Nieznany typ zadania z jednostek: ${task?.type}`);
  return mod;
}

export function generateTask(type) {
  const taskType = type || pick(Object.keys(taskRegistry));
  return { type: taskType, ...taskRegistry[taskType].generate() };
}

const taskOrder = [
  "LengthSmallToLargeTask",
  "LengthLargeToSmallTask",
  "AreaSmallToLargeTask",
  "AreaLargeToSmallTask",
  "VolumeSmallToLargeTask",
  "VolumeLargeToSmallTask",
  "MassSmallToLargeTask",
  "MassLargeToSmallTask",
];

export function generateTasks() {
  return taskOrder.map((type) => generateTask(type));
}

export const jednostkiRegistry = Object.fromEntries(
  taskOrder.map((type) => [
    type,
    {
      renderTask: (task) => renderTask({ ...task, type }),
      renderInput: (task, userAnswer, onChange, disabled) =>
        renderInput({ ...task, type }, userAnswer, onChange, disabled),
      checkAnswer: (task, userAnswer) => checkAnswer({ ...task, type }, userAnswer),
      renderAnswer: (task) => renderAnswer({ ...task, type }),
      renderExplanation: (payload) => renderExplanation({ ...payload, type }),
      renderUserAnswer: (userAnswer, task) => renderUserAnswer({ ...task, type }, userAnswer),
    },
  ])
);

export function renderTask(task) {
  return moduleFor(task).renderTask(task);
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return moduleFor(task).renderInput(task, userAnswer, onChange, disabled);
}

export function checkAnswer(task, userAnswer) {
  const expected = moduleFor(task).answer(task);
  const parsed =
    typeof userAnswer === "object" && Number.isFinite(userAnswer?.parsed)
      ? userAnswer.parsed
      : parseUserNumber(typeof userAnswer === "string" ? userAnswer : userAnswer?.raw);
  return closeTo(parsed, expected);
}

export function renderAnswer(task) {
  return moduleFor(task).renderAnswer(task);
}

export function renderUserAnswer(task, userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  if (!raw) return <i>(brak odpowiedzi)</i>;
  const mod = moduleFor(task);
  return <InlineMath math={`${formatLatexPreview(raw, mod.answerLabel)}${mod.answerSuffix}`} />;
}

export function renderExplanation(task) {
  return moduleFor(task).renderExplanation(task);
}

export const registry = jednostkiRegistry;

